import { useState } from "react";
import Icon from "../components/Icon";
import Avatar from "../components/Avatar";
import { apiBase, setApiBase } from "../api";
import type { ClientRow, Identity } from "../types";
import wordmark from "../assets/brand/wordmark-light.svg";

// API URL is only editable when no build-time base is configured. With an env
// base (the production and dev default) the control is hidden, matching the v2
// design's cleaner topbar.
const HAS_ENV_BASE = !!(import.meta as ImportMeta & { env?: { VITE_SPIM_API_BASE?: string } })
  .env?.VITE_SPIM_API_BASE;

type TopbarProps = {
  identity: Identity;
  onSignOut: () => void;
  client: ClientRow | null;
  canSwitchClient: boolean;
  onSwitchClient: () => void;
  onOpenSearch: () => void;
};

function emailInitials(email: string): string {
  const local = email.split("@")[0];
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function RoleTag({ role }: { role: string }) {
  const isOwner = role === "platform_owner";
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        textTransform: "lowercase",
        letterSpacing: "0.06em",
        padding: "2px 7px",
        borderRadius: "var(--r-pill)",
        color: isOwner ? "var(--warm-bright)" : "var(--fg-muted)",
        background: isOwner ? "rgba(244,196,90,0.14)" : "var(--surface-sunk)",
      }}
    >
      {isOwner ? "platform owner" : role.replace("client_", "")}
    </span>
  );
}

export default function Topbar({ identity, onSignOut, client, canSwitchClient, onSwitchClient, onOpenSearch }: TopbarProps) {
  const [editing, setEditing] = useState(false);
  const [urlDraft, setUrlDraft] = useState(apiBase);

  function saveUrl() {
    setApiBase(urlDraft.trim());
    setEditing(false);
    window.location.reload();
  }

  return (
    <header className="topbar">
      <div className="topbar-left">
        <div className="topbar-client">
          <span className="label">{client ? "client" : "workspace"}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span className="name">{client ? client.name : "All clients"}</span>
            {client && canSwitchClient && (
              <button onClick={onSwitchClient} className="topbar-client-picker" title="Switch client">
                <Icon name="chevronDown" size={16} />
              </button>
            )}
          </span>
        </div>

        <button
          className="btn btn-quiet btn-sm"
          onClick={onOpenSearch}
          style={{ gap: 8, color: "var(--fg-muted)" }}
          title="Search (⌘K)"
        >
          <Icon name="search" size={14} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>⌘K</span>
        </button>
      </div>

      <a href="#" className="topbar-brand" title="BeargrassAI · Service and Product Information Management" onClick={(e) => e.preventDefault()}>
        <img src={wordmark} alt="BgSPIM" />
        <span className="brand-tag">Service and Product Information Management</span>
      </a>

      <div className="topbar-right">
        {editing ? (
          <>
            <input
              className="input"
              style={{ width: 280, fontSize: 13, padding: "6px 10px" }}
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveUrl()}
              autoFocus
              placeholder="http://127.0.0.1:8787"
            />
            <button className="btn btn-primary btn-sm" onClick={saveUrl}>Save</button>
            <button className="btn btn-quiet btn-sm" onClick={() => setEditing(false)}>Cancel</button>
          </>
        ) : (
          <>
            {!HAS_ENV_BASE && (
              <button
                className="btn btn-quiet btn-sm"
                onClick={() => { setUrlDraft(apiBase()); setEditing(true); }}
                title="Configure API URL"
                style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)" }}
              >
                {apiBase() ? new URL(apiBase()).host : "no API set"}
              </button>
            )}
            <Avatar mark={emailInitials(identity.email)} size={32} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 540 }}>{identity.email}</span>
              <RoleTag role={identity.role} />
            </div>
            <span style={{ width: 1, height: 22, background: "var(--rule)", margin: "0 4px" }} />
            <button
              className="btn btn-quiet btn-sm"
              onClick={onSignOut}
              title="Sign out"
              style={{ color: "var(--fg-muted)", marginLeft: 2 }}
            >
              <Icon name="logout" size={14} />
            </button>
          </>
        )}
      </div>
    </header>
  );
}
